import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './DashboardHeader.css';
import logo from '../../assets/dashboard-logo.png';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth, db } from '../../firebase/config';
import { signOut } from 'firebase/auth';
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore';
import { getActiveRole, getActiveRoleFromStorage } from '../../utils/getActiveRole';
import { CRM_ENABLED, canUseCrm } from '../../utils/crm';
// Aliased to avoid shadowing the local getFirebaseToken() closure below
// (kept for backwards compatibility). Prefer the shared utility here so
// the on-mount fetch waits for auth to hydrate before firing.
import { getFirebaseToken as getFirebaseTokenSafe } from '../../utils/getFirebaseToken';
import NotesIcon from '../notes/NotesIcon';
import { getViewerTimezone } from '../../utils/dateTime';

// Map notification type to emoji icon
const getNotifIcon = (n) => {
  if (n.icon) return n.icon;
  const type = (n.type || n.category || '').toUpperCase();
  const map = {
    OFFER: '💰', DOCUMENT: '📄', MESSAGE: '💬', REMINDER: '🔔',
    DEADLINE: '⏰', VERIFICATION: '✅', PROPERTY: '🏠', PARTY: '🤝',
    CLOSING: '📅', TRANSACTION: '✍️',
  };
  return map[type] || '🔔';
};

// Format ISO timestamp to relative time string
const formatNotifTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  const diffMin = Math.floor((Date.now() - date) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: getViewerTimezone() });
};

// Normalize API notification to a consistent shape
const normalizeNotif = (n) => ({
  id: n.id,
  icon: getNotifIcon(n),
  title: n.title || '',
  message: n.message || n.body || n.description || '',
  time: formatNotifTime(n.createdAt || n.timestamp || n.time),
  read: n.read === true || n.isRead === true,
});

const ROLE_CONFIG = {
  ADMIN:                  { label: 'Admin',                color: '#E91E63', description: 'System Administrator' },
  AGENT:                  { label: 'Agent',                color: '#6B4EFF', description: 'Real Estate Agent' },
  BROKER:                 { label: 'Broker',               color: '#2196F3', description: 'Brokerage Manager' },
  BROKER_MANAGER:         { label: 'Broker Manager',       color: '#1565C0', description: 'Broker Manager' },
  BROKER_ADMIN:           { label: 'Broker Admin',         color: '#0D47A1', description: 'Broker Administrator' },
  BROKERAGE:              { label: 'Brokerage',            color: '#37474F', description: 'Brokerage Firm' },
  BUYER:                  { label: 'Buyer',                color: '#4CAF50', description: 'Personal Account' },
  SELLER:                 { label: 'Seller',               color: '#FF9800', description: 'Property Seller' },
  SERVICE_PROVIDER:       { label: 'Service Provider',     color: '#9C27B0', description: 'Vendor Services' },
  TRANSACTION_COORDINATOR:{ label: 'Transaction Coordinator', color: '#00BCD4', description: 'Transaction Coordinator' },
  OTHER:                  { label: 'Other',                color: '#607D8B', description: 'General Access' },
};

const PROSPECT_STATUSES = ['PENDING'];
const ACTIVE_STATUSES   = ['ACTIVE', 'UNDER_CONTRACT', 'CLOSING'];
const BROKER_ROLES      = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'];

// SVG icons per role
const RoleIcon = ({ role, size = 22 }) => {
  const icons = {
    ADMIN: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    AGENT: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      </svg>
    ),
    BROKER: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M9 8h1m-1 4h1m-1 4h1m4-8h1m-1 4h1m-1 4h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
      </svg>
    ),
    BROKER_MANAGER: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M9 8h1m-1 4h1m-1 4h1m4-8h1m-1 4h1m-1 4h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
      </svg>
    ),
    BROKER_ADMIN: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    BROKERAGE: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M9 8h1m-1 4h1m-1 4h1m4-8h1m-1 4h1m-1 4h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
        <line x1="3" y1="5" x2="21" y2="5"/>
      </svg>
    ),
    BUYER: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    SELLER: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><line x1="12" y1="22" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
      </svg>
    ),
    SERVICE_PROVIDER: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    TRANSACTION_COORDINATOR: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    OTHER: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  };
  return icons[role] || icons.OTHER;
};

const DashboardHeader = ({ activeTab = 'home' }) => {
  const navigate   = useNavigate();
  const location   = useLocation();

  const [showProfileMenu,    setShowProfileMenu]    = useState(false);
  const [showNotifications,  setShowNotifications]  = useState(false);
  const [showMobileMenu,     setShowMobileMenu]     = useState(false);
  // Standalone nav Sign Out icon (between notifications and avatar) — a faster path to
  // the same handleLogout the avatar dropdown's own Sign Out already uses, gated behind
  // a confirm step so a stray click can't sign the user out.
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [notifications,      setNotifications]      = useState([]);
  const [isNotifLoading,     setIsNotifLoading]     = useState(false);
  const [unreadCount,        setUnreadCount]        = useState(0);
  const [chatUnreadCount,    setChatUnreadCount]    = useState(0);
  const [activeNav,          setActiveNav]          = useState(activeTab);
  const [userName,           setUserName]           = useState('User');
  const [userEmail,          setUserEmail]          = useState('user@email.com');
  const [userInitials,       setUserInitials]       = useState('U');
  const [userRole,           setUserRole]           = useState(() => getActiveRoleFromStorage());
  const [userId,             setUserId]             = useState(null);
  const [profileImageUrl,    setProfileImageUrl]    = useState('');

  // Switch Role state
  const [showSwitchRoleModal, setShowSwitchRoleModal] = useState(false);
  const [availableRoles,      setAvailableRoles]      = useState([]);
  const [rolesLoading,        setRolesLoading]        = useState(false);
  const [roleCounts,          setRoleCounts]          = useState({});
  const [roleCountsLoading,   setRoleCountsLoading]   = useState(false);
  const [confirmRole,         setConfirmRole]         = useState(null);
  const [brokerCompanyName,   setBrokerCompanyName]   = useState('');
  // SP-only: vendor identity for the Service Provider row's description
  // ("Individual Inspector" vs "Company — {businessName}"), fetched from
  // the SP's own combined profile — parallel to brokerCompanyName above.
  const [spVendorType,        setSpVendorType]        = useState(null);
  const [spBusinessName,      setSpBusinessName]      = useState(null);

  // Keep userRole in sync if another component (e.g. DashboardPage) sets currentRole
  // in localStorage after this header has already mounted
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'currentRole' && e.newValue) {
        setUserRole(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Re-sync userRole on every navigation so the nav tabs stay correct after
  // same-tab redirects (e.g. DashboardPage writes currentRole then navigates).
  // The storage event only fires for cross-tab changes, so we need this too.
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role) setUserRole(role);
  }, [location]);

  // Update active nav based on current location
  useEffect(() => {
    setShowMobileMenu(false);
    const path = location.pathname;
    if (path === '/dashboard')      setActiveNav('home');
    else if (path === '/conversations') setActiveNav('conversations');
    else if (path === '/calendar')  setActiveNav('calendar');
    else if (path === '/crm' || path.startsWith('/crm/')) setActiveNav('crm');
    else if (path === '/transactions' || path.startsWith('/transaction/')) setActiveNav('transactions');
    else if (path === '/properties'   || path.startsWith('/property/'))   setActiveNav('properties');
  }, [location]);

  useEffect(() => {
    const fetchUserData = async () => {
      const storedUserData = localStorage.getItem('user');
      const firebaseToken  = localStorage.getItem('firebaseToken');

      if (storedUserData) {
        try {
          const parsedData = JSON.parse(storedUserData);
          const firstName  = parsedData.firstName || '';
          const lastName   = parsedData.lastName  || '';
          const fullName   = `${firstName} ${lastName}`.trim() || parsedData.email || 'User';
          setUserName(firstName || fullName.split(' ')[0]);
          setUserEmail(parsedData.email || 'user@email.com');
          setUserId(parsedData.id || null);

          // Read after a tick so DashboardPage (which writes currentRole on mount) wins the race
          setTimeout(() => {
            setUserRole(getActiveRole(parsedData));
          }, 0);

          const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() ||
            fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
          setUserInitials(initials);

          let profileImage = parsedData.profilePhotoBase64 || parsedData.profilePhotoUrl || parsedData.profileImageUrl || '';
          if (!profileImage && parsedData.id && firebaseToken) {
            try {
              const response = await apiClient.get(API_ENDPOINTS.userPhoto(parsedData.id), {
                headers: { 'Authorization': `Bearer ${firebaseToken}` },
                responseType: 'blob',
              });
              const contentType = response.headers['content-type'] || '';
              if (contentType.includes('image')) {
                profileImage = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(response.data);
                });
              } else {
                const text     = await response.data.text();
                const photoData = JSON.parse(text);
                profileImage = photoData.profilePhotoBase64 || photoData.photoBase64 || photoData.base64 || '';
              }
              if (profileImage) {
                parsedData.profilePhotoBase64 = profileImage;
                localStorage.setItem('user', JSON.stringify(parsedData));
              }
            } catch (photoError) {
              console.error('Failed to fetch profile photo:', photoError);
            }
          }
          setProfileImageUrl(profileImage);
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };
    fetchUserData();
  }, []);

  // Click-outside — profile menu
  useEffect(() => {
    const handler = (e) => {
      const el = document.querySelector('.dashboard-exact-profile-wrapper');
      if (el && !el.contains(e.target)) setShowProfileMenu(false);
    };
    if (showProfileMenu) setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [showProfileMenu]);

  // Click-outside — notifications
  useEffect(() => {
    const handler = (e) => {
      const el = document.querySelector('.dashboard-exact-notif-wrapper');
      if (el && !el.contains(e.target)) setShowNotifications(false);
    };
    if (showNotifications) setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [showNotifications]);

  // Click-outside — mobile nav menu (hamburger toggle + dropdown panel)
  useEffect(() => {
    const handler = (e) => {
      const panel = document.querySelector('.dashboard-exact-mobile-nav-panel');
      const btn = document.querySelector('.dashboard-exact-hamburger-btn');
      const clickedPanel = panel && panel.contains(e.target);
      const clickedBtn = btn && btn.contains(e.target);
      if (!clickedPanel && !clickedBtn) setShowMobileMenu(false);
    };
    if (showMobileMenu) setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [showMobileMenu]);

  // Fetch unread count from API — called on mount to power the badge.
  // Uses getFirebaseTokenSafe (waits for onAuthStateChanged) instead of reading
  // auth.currentUser directly: on a hard reload Firebase Auth restores the
  // persisted session asynchronously, so DashboardHeader was frequently
  // mounting and firing this request in the brief window when currentUser is
  // still null. That produced an Authorization-less request → gateway 401 →
  // browser reported it as a CORS error (401 responses skip the CORS filter,
  // masking the real cause). See rialty-notification-service gateway logs
  // for "No Bearer token found in request to: /api/v1/notifications/unread-count".
  const fetchUnreadCount = useCallback(async () => {
    let token;
    try {
      token = await getFirebaseTokenSafe();
    } catch (authError) {
      // No signed-in user (fresh page before login redirect, or 10s auth
      // timeout after Firebase persistence failed to restore). Skipping is
      // correct here — an unauthenticated user has no inbox to count.
      return;
    }
    try {
      const response = await apiClient.get(API_ENDPOINTS.notificationsUnreadCount, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const count = response.data?.unreadCount ?? response.data?.data ?? response.data ?? 0;
      setUnreadCount(typeof count === 'number' ? count : 0);
    } catch (error) {
      console.error('[DashboardHeader] Failed to fetch unread count:', error.message);
    }
  }, []);

  // Fetch unread count on mount so the badge is always up to date
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Nav-level aggregate unread-chat badge — sums unreadCount across every non-archived
  // chat in user_chats/{uid}/chats (same Firestore source ConversationPage.js reads
  // per-conversation from). Lives in DashboardHeader instead of ConversationPage so it
  // keeps updating live no matter which dashboard page is open, since DashboardHeader
  // stays mounted across route changes while ConversationPage only mounts on /conversations.
  useEffect(() => {
    let unsubChats = null;

    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (unsubChats) {
        unsubChats();
        unsubChats = null;
      }

      if (!user) {
        setChatUnreadCount(0);
        return;
      }

      const userChatsRef = query(
        collection(db, 'user_chats', user.uid, 'chats'),
        where('archived', '==', false),
        limit(100)
      );

      unsubChats = onSnapshot(
        userChatsRef,
        (snapshot) => {
          const total = snapshot.docs.reduce((sum, d) => sum + (d.data().unreadCount || 0), 0);
          setChatUnreadCount(total);
        },
        (error) => {
          console.error('[DashboardHeader] Chat unread listener error:', error.message);
        }
      );
    });

    return () => {
      unsubAuth();
      if (unsubChats) unsubChats();
    };
  }, []);

  // Fetch full notification list from API — called when dropdown opens
  const fetchNotifications = useCallback(async () => {
    try {
      setIsNotifLoading(true);
      let token = null;
      if (auth.currentUser) token = await auth.currentUser.getIdToken(true);
      const response = await apiClient.get(API_ENDPOINTS.notifications, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      const data = response.data?.notifications ?? response.data?.data ?? response.data;
      setNotifications(Array.isArray(data) ? data.map(normalizeNotif) : []);
    } catch (error) {
      console.error('[DashboardHeader] Failed to fetch notifications:', error.message);
    } finally {
      setIsNotifLoading(false);
    }
  }, []);

  const handleMarkAllRead = async () => {
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    // API call
    try {
      let token = null;
      if (auth.currentUser) token = await auth.currentUser.getIdToken(true);
      await apiClient.put(API_ENDPOINTS.markAllNotificationsRead, {}, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
    } catch (error) {
      console.error('[DashboardHeader] Failed to mark all notifications as read:', error.message);
    }
  };
  const handleMarkRead = async (id) => {
    // Optimistic update
    const wasUnread = notifications.some(n => n.id === id && !n.read);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));
    // API call
    try {
      let token = null;
      if (auth.currentUser) token = await auth.currentUser.getIdToken(true);
      await apiClient.put(API_ENDPOINTS.markNotificationRead(id), {}, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
    } catch (error) {
      console.error('[DashboardHeader] Failed to mark notification as read:', error.message);
    }
  };

  const handleLogout = async () => {
    try {
      const user  = auth.currentUser;
      let token = null;
      if (user) token = await user.getIdToken(true);
      await apiClient.post(API_ENDPOINTS.logout, {}, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
        withCredentials: true,
      });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      try { await signOut(auth); } catch (e) { console.error('Firebase sign out error:', e); }
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('firebaseToken');
      localStorage.removeItem('currentRole');
      sessionStorage.removeItem('agentVerificationData');
      sessionStorage.removeItem('inviteData');
      navigate('/login');
    }
  };

  // ── Switch Role ──────────────────────────────────────────────────────────────

  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) { auth.currentUser.getIdToken(true).then(resolve).catch(reject); return; }
      const tid = setTimeout(() => { unsub(); reject(new Error('auth timeout')); }, 10000);
      const unsub = auth.onAuthStateChanged(async (u) => {
        clearTimeout(tid); unsub();
        if (u) { try { resolve(await u.getIdToken(true)); } catch (e) { reject(e); } }
        else reject(new Error('No authenticated user'));
      });
    });
  }, []);

  const fetchRoleCounts = useCallback(async (roles, token) => {
    setRoleCountsLoading(true);
    const counts = {};

    // Pre-fetch broker transaction counts once for all broker roles using the broker-specific endpoint
    let brokerActiveCount = null;
    if (roles.some(r => BROKER_ROLES.includes(r))) {
      try {
        // Use /broker/agents (same as mobile) to get objects with userId = 20-char DB ID.
        // /broker/agents/ids returns Firebase UIDs (28-char) which the counts endpoint rejects.
        const agentsRes = await apiClient.get(API_ENDPOINTS.brokerAgents, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const raw = agentsRes.data;
        const agents = Array.isArray(raw) ? raw
          : Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw?.content) ? raw.content
          : [];
        const agentIds = agents.map(a => a.userId).filter(Boolean);

        if (agentIds.length > 0) {
          const params = new URLSearchParams();
          agentIds.forEach(id => params.append('agentUserIds', id));
          const cRes = await apiClient.get(
            `${API_ENDPOINTS.brokerTransactionCounts}?${params.toString()}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          const cData = Array.isArray(cRes.data) ? cRes.data
            : Array.isArray(cRes.data?.data) ? cRes.data.data
            : [];
          brokerActiveCount = cData.reduce((sum, c) => sum + (c.activeCount || 0), 0);
        } else {
          brokerActiveCount = 0;
        }
      } catch {
        brokerActiveCount = 0;
      }
    }

    await Promise.all(roles.map(async (role) => {
      if (BROKER_ROLES.includes(role)) {
        counts[role] = { active: brokerActiveCount ?? 0, prospect: 0 };
        return;
      }
      if (role === 'SERVICE_PROVIDER') {
        // SPs don't have "prospect transactions" — use the SP jobs API
        // instead (same status contract as JobsPage.js's TAB_STATUSES).
        try {
          const [activeResList, completedRes] = await Promise.all([
            Promise.all(
              ['ACCEPTED', 'SCHEDULED', 'IN_PROGRESS'].map(status =>
                apiClient.get(API_ENDPOINTS.spServices(status), {
                  headers: { 'Authorization': `Bearer ${token}` },
                })
              )
            ),
            apiClient.get(API_ENDPOINTS.spServices('COMPLETED'), {
              headers: { 'Authorization': `Bearer ${token}` },
            }),
          ]);
          const countOf = (d) => Array.isArray(d) ? d.length : (d?.totalElements ?? d?.content?.length ?? 0);
          counts[role] = {
            active:    activeResList.reduce((sum, r) => sum + countOf(r.data), 0),
            completed: countOf(completedRes.data),
          };
        } catch {
          counts[role] = { active: 0, completed: 0 };
        }
        return;
      }
      try {
        const [activeRes, prospectRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.transactions, {
            headers: { 'Authorization': `Bearer ${token}` },
            params: { viewingRole: role, status: ACTIVE_STATUSES.join(',') },
          }),
          apiClient.get(API_ENDPOINTS.transactions, {
            headers: { 'Authorization': `Bearer ${token}` },
            params: { viewingRole: role, status: PROSPECT_STATUSES.join(',') },
          }),
        ]);
        const activeData   = activeRes.data;
        const prospectData = prospectRes.data;
        counts[role] = {
          active:   Array.isArray(activeData)   ? activeData.length   : (activeData?.totalElements   ?? activeData?.content?.length ?? 0),
          prospect: Array.isArray(prospectData) ? prospectData.length : (prospectData?.totalElements ?? prospectData?.content?.length ?? 0),
        };
      } catch {
        counts[role] = { active: 0, prospect: 0 };
      }
    }));
    setRoleCounts(counts);
    setRoleCountsLoading(false);
  }, []);

  const handleOpenSwitchRole = async () => {
    setShowProfileMenu(false);
    setRoleCounts({});
    setConfirmRole(null);
    setBrokerCompanyName('');
    setSpVendorType(null);
    setSpBusinessName(null);

    if (!userId) return;

    setRolesLoading(true);
    setShowSwitchRoleModal(true);

    try {
      const token  = await getFirebaseToken();
      const resp   = await apiClient.get(API_ENDPOINTS.userRoles(userId), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data   = resp.data;
      let roles    = [];

      // Handle multiple possible API response formats
      const rawList = Array.isArray(data)          ? data
        : Array.isArray(data?.data)                ? data.data
        : Array.isArray(data?.content)             ? data.content
        : Array.isArray(data?.roles)               ? data.roles
        : [];

      if (rawList.length > 0) {
        roles = rawList.map(r => {
          if (typeof r === 'string')       return r;
          if (typeof r.role === 'string')  return r.role;   // {role:'AGENT'} format
          if (r.role?.name)                return r.role.name;
          if (r.roleName)                  return r.roleName;
          if (r.name)                      return r.name;
          return '';
        }).map(r => r.replace(/^ROLE_/i, '').toUpperCase()).filter(r => ROLE_CONFIG[r]);
      }

      // Fallback to localStorage — only use stored.roles if it's a non-empty array
      if (roles.length === 0) {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const storedRoles = Array.isArray(stored.roles) && stored.roles.length > 0
          ? stored.roles : stored.role ? [stored.role] : [];
        roles = storedRoles.map(r => {
          if (typeof r === 'string')       return r.replace(/^ROLE_/i, '').toUpperCase();
          if (typeof r.role === 'string')  return r.role.replace(/^ROLE_/i, '').toUpperCase();
          if (r.roleName)                  return r.roleName.replace(/^ROLE_/i, '').toUpperCase();
          return '';
        }).filter(r => ROLE_CONFIG[r]);
      }
      setAvailableRoles(roles);
      setRolesLoading(false);
      fetchRoleCounts(roles, token);

      // Fetch broker company name if BROKER role is present
      if (roles.some(r => BROKER_ROLES.includes(r))) {
        // Try localStorage first (populated when user visits profile page)
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          if (stored.companyName) {
            setBrokerCompanyName(stored.companyName);
          } else {
            // Fall back to API — use role-specific endpoint for BROKER_MANAGER / BROKER_ADMIN
            const activeRole = localStorage.getItem('currentRole') || '';
            const brokerEndpoint =
              activeRole === 'BROKER_MANAGER' ? API_ENDPOINTS.brokerManagerProfile(userId) :
              activeRole === 'BROKER_ADMIN'   ? API_ENDPOINTS.brokerAdminProfile(userId) :
              API_ENDPOINTS.brokerProfile(userId);
            const brokerResp = await apiClient.get(brokerEndpoint, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const brokerData = brokerResp.data?.companyName !== undefined
              ? brokerResp.data
              : (brokerResp.data?.data || brokerResp.data || {});
            if (brokerData.companyName) setBrokerCompanyName(brokerData.companyName);
          }
        } catch (e) {
          // Non-critical — silently ignore, description falls back to default
        }
      }

      if (roles.includes('SERVICE_PROVIDER')) {
        try {
          const spResp = await apiClient.get(API_ENDPOINTS.spProfile, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          setSpVendorType(spResp.data?.vendorType || null);
          setSpBusinessName(spResp.data?.businessName || null);
        } catch (e) {
          // Non-critical — silently ignore, description falls back to "Individual Inspector"
        }
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const storedRoles = Array.isArray(stored.roles) && stored.roles.length > 0
        ? stored.roles : stored.role ? [stored.role] : [];
      const roles = storedRoles.map(r => {
        if (typeof r === 'string')       return r.replace(/^ROLE_/i, '').toUpperCase();
        if (typeof r.role === 'string')  return r.role.replace(/^ROLE_/i, '').toUpperCase();
        if (r.roleName)                  return r.roleName.replace(/^ROLE_/i, '').toUpperCase();
        return '';
      }).filter(r => ROLE_CONFIG[r]);
      setAvailableRoles(roles);
      setRolesLoading(false);
    }
  };

  const handleConfirmSwitch = (role) => {
    setConfirmRole(prev => prev === role ? null : role);
  };

  const handleSwitchRole = async () => {
    if (!confirmRole) return;

    // For Agent/Broker roles, verify their profile exists before switching (mirrors mobile ProfileScreen)
    // BROKER_MANAGER and BROKER_ADMIN share the same broker verification flow as BROKER
    const agentLikeRoles = ['AGENT'];
    if (agentLikeRoles.includes(confirmRole) || BROKER_ROLES.includes(confirmRole)) {
      try {
        const token = await getFirebaseToken();
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const switchRoleData = {
          alreadyRegistered: true,
          firstName: stored.firstName || '',
          lastName: stored.lastName || '',
          email: stored.email || '',
        };
        if (agentLikeRoles.includes(confirmRole)) {
          const res = await apiClient.get(API_ENDPOINTS.agentProfile(userId), {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const profile = res.data?.data || res.data?.content || res.data;
          if (!profile?.licenseVerified && !profile?.licenseNumber) {
            setShowSwitchRoleModal(false);
            setConfirmRole(null);
            navigate('/agent-verification', { state: { inviteData: switchRoleData } });
            return;
          }
        } else {
          const res = await apiClient.get(API_ENDPOINTS.brokerProfile(userId), {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const profile = res.data?.data || res.data?.content || res.data;
          if (!profile?.brokerLicenseNumber && !profile?.companyLicenseNumber) {
            setShowSwitchRoleModal(false);
            setConfirmRole(null);
            navigate('/broker-verification', { state: { inviteData: switchRoleData } });
            return;
          }
        }
      } catch (err) {
        // 404 means no profile exists — redirect to verification
        if (err.response?.status === 404) {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          const switchRoleData = {
            alreadyRegistered: true,
            firstName: stored.firstName || '',
            lastName: stored.lastName || '',
            email: stored.email || '',
          };
          setShowSwitchRoleModal(false);
          setConfirmRole(null);
          navigate(BROKER_ROLES.includes(confirmRole) ? '/broker-verification' : '/agent-verification',
            { state: { inviteData: switchRoleData } });
          return;
        }
        // Other errors: allow the switch so the user isn't blocked unexpectedly
      }
    }

    // Notify backend of the active role change (mirrors mobile setActiveRole)
    if (userId) {
      try {
        const token = await getFirebaseToken();
        await apiClient.put(
          API_ENDPOINTS.setActiveRole(userId),
          { role: confirmRole },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Error setting active role on backend:', err);
      }
    }

    // Persist to localStorage
    localStorage.setItem('currentRole', confirmRole);
    // Update user object roles[0] so all other pages pick it up immediately
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const allRoles = Array.isArray(stored.roles) ? stored.roles : [stored.roles || stored.role].filter(Boolean);
      // Reorder so selected role is first
      const reordered = [confirmRole, ...allRoles.filter(r => r.toUpperCase() !== confirmRole)];
      stored.roles = reordered;
      stored.role  = confirmRole;
      localStorage.setItem('user', JSON.stringify(stored));
    } catch (e) { /* ignore */ }
    setUserRole(confirmRole);
    setShowSwitchRoleModal(false);
    setConfirmRole(null);
    // Route to the correct dashboard based on the new role — this header
    // never renders on the Service Provider's own pages (see
    // SpDashboardHeader), so switching TO Service Provider is the only SP
    // case left to handle here.
    const isBrokeragePage = location.pathname === '/dashboard/brokerage';
    if (confirmRole === 'SERVICE_PROVIDER') {
      navigate('/dashboard/sp', { replace: true });
    } else if (isBrokeragePage) {
      navigate('/dashboard', { replace: true });
    } else if (location.pathname.startsWith('/transaction')) {
      navigate('/dashboard', { replace: true });
    } else {
      navigate(0);
    }
  };

  const closeSwitchRoleModal = () => {
    setShowSwitchRoleModal(false);
    setConfirmRole(null);
    setAvailableRoles([]);
  };

  const currentRoleNorm = userRole?.toUpperCase();

  const roleLabel = (role) => {
    if (!role) return '';
    const r = role?.toUpperCase();
    const cfg = ROLE_CONFIG[r];
    if (cfg) return `${cfg.label} Account`;
    // Format unknown role: "BROKER_ADMIN" → "Broker Admin Account"
    const formatted = r.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    return `${formatted} Account`;
  };

  // Nav tab buttons — shared between the desktop pill and the mobile/tablet
  // dropdown panel so both stay in sync from one source. Layout-only reuse;
  // no colors are set here, both containers inherit whatever the page's
  // existing CSS already defines for .dashboard-exact-nav-tab.
  // This header never renders for the Service Provider role (see
  // SpDashboardHeader.js), so only the default nav is needed here.
  // CRM tab: agents and brokerages only (the service enforces this too), and only when the build
  // flag is on. With it the nav has six tabs, which needs the compact layout in DashboardHeader.css.
  const showCrmTab = CRM_ENABLED && canUseCrm(currentRoleNorm);

  const navTabsContent = (
    <>
      <button
        className={`dashboard-exact-nav-tab ${activeNav === 'home' ? 'active' : ''}`}
        onClick={() => { setActiveNav('home'); navigate('/dashboard'); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
          <path d="M9 21V12h6v9"/>
        </svg>
        Home
      </button>

      {showCrmTab && (
        <button
          className={`dashboard-exact-nav-tab ${activeNav === 'crm' ? 'active' : ''}`}
          onClick={() => { setActiveNav('crm'); navigate('/crm'); }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          Connect
        </button>
      )}

      <button
        className={`dashboard-exact-nav-tab ${activeNav === 'conversations' ? 'active' : ''}`}
        onClick={() => { setActiveNav('conversations'); navigate('/conversations'); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
          <path d="M2 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8l-3 3v-3H4a2 2 0 0 1-2-2V4z"
            fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
          <path d="M8 9a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2v3l-3-3h-5a2 2 0 0 1-2-2V9z"
            fill="#EEEAF8" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
          <line x1="12" y1="12" x2="18" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="12" y1="15" x2="16" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        Conversations
        {chatUnreadCount > 0 && (
          <span className="dashboard-exact-nav-badge">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>
        )}
      </button>

      <button
        className={`dashboard-exact-nav-tab ${activeNav === 'transactions' ? 'active' : ''}`}
        onClick={() => { setActiveNav('transactions'); navigate('/transactions'); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
          <line x1="12" y1="12" x2="12" y2="16"/>
          <line x1="10" y1="14" x2="14" y2="14"/>
        </svg>
        Transactions
      </button>

      <button
        className={`dashboard-exact-nav-tab ${activeNav === 'properties' ? 'active' : ''}`}
        onClick={() => { setActiveNav('properties'); navigate('/properties'); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
        </svg>
        Properties
      </button>

      <button
        className={`dashboard-exact-nav-tab ${activeNav === 'calendar' ? 'active' : ''}`}
        onClick={() => { setActiveNav('calendar'); navigate('/calendar'); }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        Calendar
      </button>
    </>
  );

  return (
    <>
      {/* Overlay for profile menu */}
      {showProfileMenu && (
        <div className="dashboard-exact-overlay show" onClick={() => setShowProfileMenu(false)} />
      )}

      {/* Standalone Sign Out confirm — triggered by the nav-bar icon (between notifications
          and the avatar), separate from the avatar dropdown's own Sign Out item. */}
      {showSignOutConfirm && (
        <div className="signout-confirm-overlay" onClick={() => setShowSignOutConfirm(false)}>
          <div className="signout-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signout-confirm-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <h3 className="signout-confirm-title">Sign out of Rialty?</h3>
            <p className="signout-confirm-subtitle">You'll need to log back in to access your transactions and account.</p>
            <div className="signout-confirm-actions">
              <button className="signout-confirm-cancel" onClick={() => setShowSignOutConfirm(false)}>
                Cancel
              </button>
              <button className="signout-confirm-signout" onClick={() => { setShowSignOutConfirm(false); handleLogout(); }}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Switch Role Modal Overlay */}
      {showSwitchRoleModal && (
        <div className="sr-modal-overlay" onClick={closeSwitchRoleModal}>
          <div className="sr-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sr-modal-header">
              <div className="sr-modal-header-left">
                <div className="sr-modal-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div>
                  <h2 className="sr-modal-title">Switch Role</h2>
                  <p className="sr-modal-subtitle">Select an account role to switch to</p>
                </div>
              </div>
              <button className="sr-modal-close" onClick={closeSwitchRoleModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="sr-modal-body">
              {rolesLoading ? (
                <div className="sr-loading">
                  <div className="sr-spinner"></div>
                  <span>Loading roles...</span>
                </div>
              ) : availableRoles.length === 0 ? (
                <div className="sr-empty">No roles available for this account.</div>
              ) : (
                <div className="sr-roles-list">
                  {availableRoles.map((role) => {
                    const cfg       = ROLE_CONFIG[role] || ROLE_CONFIG.OTHER;
                    const isCurrent = role === currentRoleNorm;
                    const isSelected = confirmRole === role;
                    const counts    = roleCounts[role];

                    return (
                      <div
                        key={role}
                        className={`sr-role-card${isCurrent ? ' sr-role-current' : ''}${isSelected ? ' sr-role-selected' : ''}`}
                        onClick={() => !isCurrent && handleConfirmSwitch(role)}
                      >
                        <div className="sr-role-icon" style={{ background: cfg.color + '22', color: cfg.color }}>
                          <RoleIcon role={role} size={22} />
                        </div>
                        <div className="sr-role-info">
                          <div className="sr-role-name">{cfg.label}</div>
                          <div className="sr-role-desc">
                            {role === 'SERVICE_PROVIDER'
                              ? (spVendorType === 'COMPANY'
                                  ? `Company — ${spBusinessName || cfg.label}`
                                  : 'Individual Inspector')
                              : BROKER_ROLES.includes(role) && brokerCompanyName
                                ? brokerCompanyName
                                : cfg.description}
                          </div>
                        </div>
                        <div className="sr-role-right">
                          {isCurrent && (
                            <span className="sr-current-badge">CURRENT</span>
                          )}
                          {!isCurrent && isSelected && (
                            <span className="sr-selected-badge">SELECTED</span>
                          )}
                          {counts ? (
                            <div className="sr-role-stats">
                              <div className="sr-role-stat">
                                <span className="sr-stat-val">{roleCountsLoading ? '—' : counts.active}</span>
                                <span className="sr-stat-lbl">Active</span>
                              </div>
                              {!BROKER_ROLES.includes(role) && (
                                <div className="sr-role-stat">
                                  <span className="sr-stat-val">
                                    {roleCountsLoading ? '—' : (role === 'SERVICE_PROVIDER' ? counts.completed : counts.prospect)}
                                  </span>
                                  <span className="sr-stat-lbl">{role === 'SERVICE_PROVIDER' ? 'Completed' : 'Prospect'}</span>
                                </div>
                              )}
                            </div>
                          ) : roleCountsLoading ? (
                            <div className="sr-counts-loading">
                              <div className="sr-spinner-small"></div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — confirm button */}
            {confirmRole && confirmRole !== currentRoleNorm && (
              <div className="sr-modal-footer">
                <p className="sr-confirm-text">
                  You are switching to <strong>{ROLE_CONFIG[confirmRole]?.label || confirmRole}</strong> role.
                </p>
                <div className="sr-footer-btns">
                  <button className="sr-btn-cancel" onClick={() => setConfirmRole(null)}>Cancel</button>
                  <button className="sr-btn-switch" onClick={handleSwitchRole}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
                      <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
                    </svg>
                    Switch Role
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <nav className={`dashboard-exact-nav${showCrmTab ? ' dashboard-exact-nav--crm' : ''}`}>
        <div className="dashboard-exact-nav-content">
          <div className="dashboard-exact-logo-wrapper" onClick={() => navigate('/dashboard')}>
            <div className="dashboard-exact-logo">
              <img src={logo} alt="Rialty.AI" className="dashboard-exact-logo-img" />
            </div>
          </div>

          <div className="dashboard-exact-nav-tabs">
            {navTabsContent}
          </div>

          <div className="dashboard-exact-nav-right">
            {/* Hamburger toggle — mobile & tablet only (nav pill is hidden below that breakpoint) */}
            <button
              className={`dashboard-exact-hamburger-btn${showMobileMenu ? ' active' : ''}`}
              aria-label="Toggle navigation menu"
              onClick={() => {
                setShowMobileMenu(prev => !prev);
                setShowProfileMenu(false);
                setShowNotifications(false);
              }}
            >
              {showMobileMenu ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              )}
            </button>

            {/* My Notes — private, user-scoped notes; visible from every screen per BP's direction (not in the main nav bar or Quick Actions) */}
            <button
              className="dashboard-exact-notes-btn"
              aria-label="My Notes"
              title="My Notes"
              onClick={() => {
                navigate('/my-notes');
                setShowMobileMenu(false);
                setShowNotifications(false);
                setShowProfileMenu(false);
              }}
            >
              <NotesIcon size={18} strokeWidth={2.2} />
            </button>

            {/* Notification Bell */}
            <div className="dashboard-exact-notif-wrapper">
              <button
                className={`dashboard-exact-notification-btn${showNotifications ? ' active' : ''}`}
                onClick={() => {
                  const next = !showNotifications;
                  setShowNotifications(next);
                  setShowMobileMenu(false);
                  if (next) fetchNotifications();
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" fill="currentColor"/>
                </svg>
                {unreadCount > 0 && (
                  <span className="dashboard-exact-notification-badge">{unreadCount}</span>
                )}
              </button>

              {showNotifications && (
                <div className="dashboard-exact-notif-panel">
                  <div className="dashboard-exact-notif-panel-header">
                    <div>
                      <h3>Latest Updates</h3>
                      <p>{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</p>
                    </div>
                    <button className="dashboard-exact-mark-all-btn" onClick={handleMarkAllRead}>Mark all read</button>
                  </div>
                  <div className="dashboard-exact-notif-list">
                    {isNotifLoading ? (
                      <div className="dashboard-exact-notif-loading">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                        </svg>
                        Loading...
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="dashboard-exact-notif-empty">No notifications yet</div>
                    ) : (
                      notifications.slice(0, 3).map(n => (
                        <div
                          key={n.id}
                          className={`dashboard-exact-notif-item${!n.read ? ' unread' : ''}`}
                          onClick={() => handleMarkRead(n.id)}
                        >
                          {!n.read && <div className="dashboard-exact-notif-dot"></div>}
                          <div className="dashboard-exact-notif-icon">{n.icon}</div>
                          <div className="dashboard-exact-notif-content">
                            <div className="dashboard-exact-notif-top">
                              <span className={`dashboard-exact-notif-title${n.read ? ' read' : ''}`}>{n.title}</span>
                              <span className="dashboard-exact-notif-time">{n.time}</span>
                            </div>
                            <p className="dashboard-exact-notif-msg">{n.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="dashboard-exact-notif-footer">
                    <button className="dashboard-exact-view-all-btn" onClick={() => { setShowNotifications(false); navigate('/notifications'); }}>
                      See all notifications →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Sign Out — standalone nav icon, sits at the same level as notifications (not
                nested inside the avatar dropdown). One click opens the confirm above; the
                avatar dropdown keeps its own Sign Out too — this is an additional, faster path. */}
            <button
              className="dashboard-exact-signout-btn"
              aria-label="Sign Out"
              title="Sign Out"
              onClick={() => { setShowSignOutConfirm(true); setShowMobileMenu(false); setShowNotifications(false); setShowProfileMenu(false); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>

            {/* Profile Menu */}
            <div className="dashboard-exact-profile-wrapper" style={{ position: 'relative' }}>
              <button
                className="dashboard-exact-profile-btn"
                onClick={() => { setShowProfileMenu(!showProfileMenu); setShowMobileMenu(false); }}
              >
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt="Profile" className="dashboard-exact-avatar-img" />
                ) : (
                  <div className="dashboard-exact-avatar">{userInitials}</div>
                )}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              <div
                className={`dashboard-exact-profile-menu ${showProfileMenu ? 'show' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Scrollable region — everything except the sticky Sign Out footer below,
                    so Sign Out is always visible on open without scrolling (mirrors
                    Slack/Gmail account menus). */}
                <div className="dashboard-exact-profile-menu-scroll">
                {/* Menu Header */}
                <div className="dashboard-exact-profile-menu-header">
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profile" className="dashboard-exact-profile-menu-avatar-img" />
                  ) : (
                    <div className="dashboard-exact-profile-menu-avatar">{userInitials}</div>
                  )}
                  <div className="dashboard-exact-profile-menu-info">
                    <div className="dashboard-exact-profile-menu-name" title={userName}>{userName}</div>
                    <div className="dashboard-exact-profile-menu-email" title={userEmail}>{userEmail}</div>
                    <div className="dashboard-exact-profile-menu-role">
                      {roleLabel(userRole)}
                    </div>
                  </div>
                </div>

                <div className="dashboard-exact-profile-menu-divider"></div>

                {/* My Profile */}
                <div
                  className="dashboard-exact-profile-menu-item dashboard-exact-profile-option"
                  onClick={() => { setShowProfileMenu(false); navigate('/profile'); }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>My Profile</span>
                </div>

                {/* Switch Role */}
                <div
                  className="dashboard-exact-profile-menu-item dashboard-exact-profile-option dashboard-exact-switch-role-item"
                  onClick={handleOpenSwitchRole}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>Switch Role</span>
                  <span className="dashboard-exact-current-role-badge">
                    {ROLE_CONFIG[currentRoleNorm]?.label || userRole}
                  </span>
                </div>

                {/* Invite Someone */}
                <div
                  className="dashboard-exact-profile-menu-item dashboard-exact-profile-option"
                  onClick={() => { setShowProfileMenu(false); navigate('/invite-someone'); }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="19" y1="8" x2="19" y2="14"/>
                    <line x1="22" y1="11" x2="16" y2="11"/>
                  </svg>
                  <span>Invite Someone</span>
                </div>

                </div>
                {/* /dashboard-exact-profile-menu-scroll */}
                {/* Notification Settings, Payments & Payouts, Connected Services, Activity
                    Log, Security & Settings, Privacy Policy, and Terms of Service used to be
                    separate items here — they now live as sections inside My Profile's left
                    sidenav (see ProfilePage.js) so this menu stays a fixed 4 items. */}

                {/* Sticky Sign Out footer — pinned outside the scrollable item list above,
                    always visible immediately on open (no scrolling required). */}
                <div className="dashboard-exact-profile-menu-footer">
                  <div className="dashboard-exact-profile-menu-divider"></div>
                  <div
                    className="dashboard-exact-profile-menu-item dashboard-exact-logout-item"
                    onClick={handleLogout}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    <span>Sign Out</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile / tablet nav dropdown — hamburger toggle target, hidden on desktop.
           No colors set in JS/CSS for this — it inherits whatever .dashboard-exact-nav-tab
           already looks like on each page, laid out as a vertical list instead of a pill. */}
        <div
          className={`dashboard-exact-mobile-nav-panel${showMobileMenu ? ' show' : ''}`}
          onClick={() => setShowMobileMenu(false)}
        >
          {navTabsContent}
        </div>
      </nav>
    </>
  );
};

export default DashboardHeader;
